import { ReactNode, useState } from 'react';
import { StoreWrapper as LegacyStoreWrapper } from '@applyboard/ab-common';
import {
  LegacyApplicationDataLoader,
  LegacyInitialPropsContext,
  LegacyStudentRecords,
  LegacyCustomFieldsTab,
  LegacyCustomFieldsTabBar,
  legacyGetTemplateString,
  LegacyMultipleNotesModal,
} from '@applyboard/ab-application-legacy';
import { useParams, useRouteMatch } from 'react-router-dom';
import {
  useApplication,
  useApplicationAvailableActions,
  useApplicationPageInitialProps,
  useFeatureFlags,
} from './data';
import { assert } from '~/utils/assert';
import { Box, Card, Flex, Tabs } from '@applyboard/crystal-ui';
import {
  MAX_PAGE_WIDTH,
  GLOBAL_TOP_BAR_HEIGHT,
  MINI_HEADER_HEIGHT,
  GUTTER_WIDTH_TOKEN,
} from './consts';
import { match } from 'ts-pattern';
import { ApplicationActionName } from '~/types';
import { useNotes } from './data/notes';
import { ApplicationCreatedDialog } from './components/ApplicationCreatedDialog';
import { ApplicationPageTracker } from './components/ApplicationPageTracker';
import { ApplicationStepperCard } from './components/ApplicationStepper';
import { HeaderProvider } from './components/Header';
import { NotesListProvider, useNoteForm } from './components/NotesList';
import { RecommendedProgramsDialogLoader } from './components/RecommendedProgramsDialog';
import { RequirementList } from './components/RequirementList';
import { SideBarCard, SidebarProvider } from './components/SideBar';

export interface StudentApplicationPageLayoutProps {
  match: {
    params: {
      application_id: string;
      requirement_id?: string;
    };
  };
  commissionTab?: ReactNode;
  header?: ReactNode;
  sidebar?: ReactNode;
  notesList?: ReactNode;
  threeSixtySolutionsAd?: ReactNode;
}

const SIDEBAR_MARGIN = GUTTER_WIDTH_TOKEN * 4;
const SIDEBAR_BORDER = 1;
const SIDEBAR_MAX_HEIGHT = `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px - ${MINI_HEADER_HEIGHT}px - ${SIDEBAR_MARGIN * 2}px - ${SIDEBAR_BORDER * 2}px)`;
const SIDEBAR_TOP = GLOBAL_TOP_BAR_HEIGHT + MINI_HEADER_HEIGHT + SIDEBAR_MARGIN;
const STEPPER_MAX_HEIGHT = `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px - ${MINI_HEADER_HEIGHT}px - ${SIDEBAR_MARGIN * 2}px - ${SIDEBAR_BORDER * 2}px)`;

// TODO: Remove "View as" switcher

export function StudentApplicationPageLayout({
  commissionTab,
  header,
  sidebar,
  notesList,
  threeSixtySolutionsAd,
}: StudentApplicationPageLayoutProps) {
  const { requirement_id: requirementId } = useParams<{ requirement_id?: string }>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(!requirementId);
  const { initialProps } = useApplicationPageInitialProps();
  const { application } = useApplication();

  assert(initialProps);

  const tab = useCurrentTab();
  const checkCanCreateBulkNote = useCanCreateBulkNote();

  const { applicationNotes: customerNotes } = useNotes('application_customer_note');
  const { applicationNotes: adminNotes } = useNotes('application_admin_note');
  const numCustomerNotes = customerNotes.length;
  const numAdminNotes = adminNotes.length;

  const noteForm = useNoteForm();
  const adminNoteForm = useNoteForm();

  const { canPerformApplicationAction } = useApplicationAvailableActions();
  const showCommissionTab =
    commissionTab && canPerformApplicationAction(ApplicationActionName.VIEW_COMMISSION);

  return (
    <SidebarProvider isOpen={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <ApplicationPageTracker applicationId={application.legacyId}>
        {/*
         * TODO: Remove StoreWrapper.  Unfortunately it's currently needed for LegacyStatusCheckboxes, NoteTemplatesModal, LegacyStudentRecords, and LegacyCustomFieldsTab
         */}
        <LegacyStoreWrapper>
          {/*
           * TODO: Remove LegacyInitialPropsContext.  Unfortunately it's currently needed for LegacyStatusCheckboxes, and LegacyCustomFieldsTab
           */}
          <LegacyInitialPropsContext.Provider value={initialProps}>
            {/**
             * TODO: Remove LegacyApplicationDataLoader.  Unfortunately it's currently needed for LegacyStatusCheckboxes, LegacyStudentRecords, and LegacyCustomFieldsTab, which uses data that is populated into redux via LegacyApplicationDataLoader
             */}
            <LegacyApplicationDataLoader
              applicationId={application.legacyId}
              permissions={initialProps.permissions}
            >
              <HeaderProvider sidebar={sidebar} threeSixtySolutionsAd={threeSixtySolutionsAd}>
                {header}
              </HeaderProvider>
              <div
                css={theme => ({
                  width: '100%',
                  paddingLeft: theme.spacing[GUTTER_WIDTH_TOKEN],
                  paddingRight: theme.spacing[GUTTER_WIDTH_TOKEN],
                  paddingTop: theme.spacing[GUTTER_WIDTH_TOKEN],
                  maxWidth: MAX_PAGE_WIDTH,
                  margin: 'auto',
                  '@media (max-width: 600px)': {
                    backgroundColor: theme.colors.bgRegular,
                    paddingLeft: 0,
                    paddingRight: 0,
                  },
                })}
              >
                <Flex direction="row" gap={GUTTER_WIDTH_TOKEN}>
                  {sidebar && (
                    <div
                      css={{
                        flexGrow: 0,
                        [`@media (max-width: ${requirementId ? '1279px' : '1119px'})`]: {
                          display: 'none',
                        },
                      }}
                    >
                      <div
                        style={{
                          position: 'sticky',
                          top: SIDEBAR_TOP,
                          maxHeight: `calc(${SIDEBAR_MAX_HEIGHT} + 4px)`,
                          // Fixes AC #2 here: https://applyboard.atlassian.net/browse/AWA-6035
                          overflowY: 'hidden',
                        }}
                      >
                        <SideBarCard maxHeight={SIDEBAR_MAX_HEIGHT}>{sidebar}</SideBarCard>
                      </div>
                    </div>
                  )}
                  <Flex.Item grow={1} shrink={1} basis={0} minWidth={0}>
                    <div style={{ minHeight: `calc(${SIDEBAR_MAX_HEIGHT} + 4px)`, height: '100%' }}>
                      <Card height="fill" emphasis={{ xs: 'plain', sm: 'outlined' }}>
                        <Box px={{ xs: 0, sm: 2 }}>
                          <Tabs value={tab}>
                            <Tabs.Item
                              label="Requirements"
                              value="requirements"
                              href={`/applications/${application.legacyId}/requirements`}
                            >
                              <RequirementList
                                requirementClassifications={['applicant_requirement']}
                              />
                            </Tabs.Item>
                            {initialProps.permissions.show_actions_tab && (
                              <Tabs.Item
                                label="Admin requirements"
                                value="admin-requirements"
                                href={`/applications/${application.legacyId}/admin-requirements`}
                              >
                                <RequirementList
                                  requirementClassifications={[
                                    'action_requirement',
                                    'school_requirement',
                                  ]}
                                />
                              </Tabs.Item>
                            )}
                            {initialProps.permissions.view_student_records && (
                              <Tabs.Item
                                label="Student records"
                                value="student-records"
                                href={`/applications/${application.legacyId}/student-records`}
                              >
                                <LegacyStudentRecords />
                              </Tabs.Item>
                            )}
                            {initialProps.permissions.index_public_note && (
                              <Tabs.Item
                                label={`Notes${numCustomerNotes > 0 ? ` (${numCustomerNotes})` : ''}`}
                                value="notes"
                                href={`/applications/${application.legacyId}/notes`}
                              >
                                <NotesListProvider
                                  noteType="customer"
                                  noteForm={noteForm}
                                  onPrefillTemplate={(isPrivate, trackEvent) => {
                                    return legacyGetTemplateString({
                                      isPrivate,
                                      trackEvent,
                                      canCreateBulkNote: checkCanCreateBulkNote,
                                      isMarkdownRendererEnabled: true,
                                      noteType: 'customer',
                                    });
                                  }}
                                >
                                  {notesList}
                                </NotesListProvider>
                              </Tabs.Item>
                            )}
                            {initialProps.permissions.index_private_note && (
                              <Tabs.Item
                                label={`Admin comments${numAdminNotes > 0 ? ` (${numAdminNotes})` : ''}`}
                                value="admin-comments"
                                href={`/applications/${application.legacyId}/admin-comments`}
                              >
                                <NotesListProvider
                                  noteType="admin"
                                  noteForm={adminNoteForm}
                                  onPrefillTemplate={(isPrivate, trackEvent) => {
                                    return legacyGetTemplateString({
                                      isPrivate,
                                      trackEvent,
                                      canCreateBulkNote: checkCanCreateBulkNote,
                                      isMarkdownRendererEnabled: true,
                                      noteType: 'admin',
                                    });
                                  }}
                                >
                                  {notesList}
                                </NotesListProvider>
                              </Tabs.Item>
                            )}
                            {(initialProps.permissions.show_custom_field_student ||
                              initialProps.permissions.show_custom_field_application) && (
                              <Tabs.Item
                                label="Application details"
                                value="application-details"
                                href={`/applications/${application.legacyId}/application-details`}
                              >
                                {(initialProps.permissions.create_custom_field_student ||
                                  initialProps.permissions.create_custom_field_application) && (
                                  <LegacyCustomFieldsTabBar enableAddCustomFields />
                                )}
                                <LegacyCustomFieldsTab />
                              </Tabs.Item>
                            )}
                            {showCommissionTab && (
                              <Tabs.Item
                                label="Commission"
                                value="commission"
                                href={`/applications/${application.legacyId}/commission`}
                              >
                                {commissionTab}
                              </Tabs.Item>
                            )}
                          </Tabs>
                        </Box>
                      </Card>
                    </div>
                  </Flex.Item>
                  {!requirementId && (
                    <div
                      css={{
                        flexGrow: 0,
                        '@media (max-width: 1507px)': {
                          display: 'none',
                        },
                      }}
                    >
                      <div
                        style={{
                          position: 'sticky',
                          top: SIDEBAR_TOP,
                          maxHeight: `calc(${SIDEBAR_MAX_HEIGHT} + 4px)`,
                          // Fixes AC #2 here: https://applyboard.atlassian.net/browse/AWA-6035
                          overflowY: 'hidden',
                        }}
                      >
                        <ApplicationStepperCard
                          maxHeight={STEPPER_MAX_HEIGHT}
                          threeSixtySolutionsAd={threeSixtySolutionsAd}
                        />
                      </div>
                    </div>
                  )}
                </Flex>
              </div>
              {/* legacyGetTemplateString() requires an element with this ID to function correctly */}
              <div id="note-templates-modal-container" />
              <LegacyMultipleNotesModal
                isMarkdownRendererEnabled
                noteType={match(tab)
                  .with('admin-comments', () => 'admin' as const)
                  .with('notes', () => 'customer' as const)
                  .otherwise(() => undefined)}
              />
              <ApplicationCreatedDialog />
              <RecommendedProgramsDialogLoader />
            </LegacyApplicationDataLoader>
          </LegacyInitialPropsContext.Provider>
        </LegacyStoreWrapper>
      </ApplicationPageTracker>
    </SidebarProvider>
  );
}

function useCurrentTab() {
  const { initialProps } = useApplicationPageInitialProps();
  assert(initialProps);

  // If the user has access to the admin comments tab, it should default to the
  // admin comments tab if there's no subpath.  Feels weird to change the
  // default tab based off permissions (what happens if someone sends the link
  // to someone with less permissions, assuming they'll see what they're looking
  // at), but apparently it's important for operations
  // See: https://applyboard.slack.com/archives/C06ME2XT59V/p1748626402935119?thread_ts=1748613459.076099&cid=C06ME2XT59V
  const defaultTab = initialProps.permissions.index_private_note
    ? 'admin-comments'
    : 'requirements';

  const routeMatch = useRouteMatch([
    '/applications/:application_id/requirements',
    '/applications/:application_id/admin-requirements',
    '/applications/:application_id/notes',
    '/applications/:application_id/admin-comments',
    '/applications/:application_id/student-records',
    '/applications/:application_id/application-details',
    '/applications/:application_id/commission',
  ]);

  return match(routeMatch?.path)
    .with('/applications/:application_id/requirements', () => 'requirements')
    .with('/applications/:application_id/admin-requirements', () => 'admin-requirements')
    .with('/applications/:application_id/notes', () => 'notes')
    .with('/applications/:application_id/admin-comments', () => 'admin-comments')
    .with('/applications/:application_id/student-records', () => 'student-records')
    .with('/applications/:application_id/application-details', () => 'application-details')
    .with('/applications/:application_id/commission', () => 'commission')
    .otherwise(() => defaultTab);
}

export function useCanCreateBulkNote() {
  const { featureFlags } = useFeatureFlags();
  const { getApplicationAvailableAction } = useApplicationAvailableActions();
  const adminCreateNoteAction = getApplicationAvailableAction(
    ApplicationActionName.CREATE_ADMIN_NOTE,
  );
  const customerCreateNoteAction = getApplicationAvailableAction(
    ApplicationActionName.CREATE_CUSTOMER_NOTE,
  );

  return function canCreateBulkNote(
    permissions: Record<string, boolean> | false,
    noteType: 'admin' | 'customer',
  ) {
    if (featureFlags['application_apr_notes']) {
      return noteType === 'admin'
        ? !!adminCreateNoteAction?.additionalInformation?.bulkCreateAllowed
        : !!customerCreateNoteAction?.additionalInformation?.bulkCreateAllowed;
    } else {
      return permissions && permissions['bulk_create_public_note'];
    }
  };
}
